import React from 'react'
import { connect } from 'react-redux'
import { withStyles } from '@mui/styles'

import lang from '@/lang'
import { ModalSmall } from '@/modules/base/components/modal/index'
import { BankingTextInput } from '@/modules/banking/components/inputs'
import { CancelButton, ButtonIconSquare } from '@/modules/base/components/buttons/index'
import Loader from '@/modules/base/components/loader/loader'
import { BankingService } from '@/modules/banking/services/index'
import { ChipTan, PhotoTan } from '@/modules/banking/components/tan'
import {
  openBankAccountSetupTanInput,
  closeBankAccountSetupTanInput,
} from '@/redux/ducks/banking/bankAccountSetup'

import IconCheck from '@assets/images/icons/check.svg'

import { FINANCE_API } from '@/modules/banking/constants'
import theme from '@/theme'

const { TAN_CALLBACK_TYPES } = FINANCE_API

const TAN_COMPONENT_TYPE_NONE = 'none'
const TAN_COMPONENT_TYPE_CHIPTAN = 'chipTAN'
const TAN_COMPONENT_TYPE_PHOTOTAN = 'photoTAN'

const isDecoupledTanCallback = (tanCallbackType) => [TAN_CALLBACK_TYPES.DECOUPLED_AUTOMATIC, TAN_CALLBACK_TYPES.DECOUPLED_MANUAL].includes(tanCallbackType)

class TanModal extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      loading: false,
      tan: undefined,
      tanErrorMessage: null,
      useModal: true,
    }

    this.handleChange = this.handleChange.bind(this)
    this.onCancel = this.onCancel.bind(this)
    this.onConfirm = this.onConfirm.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
    this.renderTANComponent = this.renderTANComponent.bind(this)
  }

  componentWillUnmount() {
    this.setState({ tan: undefined, tanErrorMessage: undefined })
  }

  componentDidMount() {
    const {
      transaction,
    } = this.props
    if (
      this.props.bankAccountSetup
      && this.props.bankAccountSetup.showBankAccountSetupView
    ) {
      this.props.openBankAccountSetupTanInput(transaction.provideTanRequest)
      this.setState({ useModal: false })
    }
  }

  componentDidUpdate() {
    if (!this.state.useModal && this.props.bankAccountSetup && this.props.bankAccountSetup.showBankAccountSetupView) {
      if (this.props.bankAccountSetup.errorBankAccountSetupTanInput) {
        const {
          onError,
        } = this.props
        onError(this.props.bankAccountSetup.errorBankAccountSetupTanInput)
      } else if (this.props.bankAccountSetup.abortBankAccountSetupTanInput) {
        const {
          onAbort,
        } = this.props
        onAbort()
      } else if (this.props.bankAccountSetup.successBankAccountSetupTanInput) {
        const {
          transaction,
          onSuccess,
        } = this.props
        onSuccess(transaction)
        this.props.closeBankAccountSetupTanInput()
      }
    }
  }

  handleChange(name) {
    return (event) => {
      this.setState({ [name]: event.target.value })
    }
  }

  onConfirm() {
    const { tan } = this.state
    const {
      transaction,
      onSuccess,
      onError,
      processId,
      transaction: {
        provideTanRequest: {
          token,
          tanCallbackType,
        },
      },
    } = this.props

    if (!tan && !isDecoupledTanCallback(tanCallbackType)) {
      return this.setState({ tanErrorMessage: lang.bankMissingTanErrorMessage })
    }

    this.setState({ loading: true }, () => {
      return BankingService.psd2.callbacks.tan(token, { tan: tan || '' }, processId)
        .then(() => {
          this.setState({ loading: false }, () => {
            onSuccess(transaction)
          })
        })
        .catch(onError)
    })
  }

  onCancel() {
    const { onAbort } = this.props
    onAbort()
  }

  onSubmit(event) {
    event.preventDefault()
    this.onConfirm()
  }

  renderTANComponent() {
    const {
      classes,
      transaction: {
        provideTanRequest: {
          challengeHhduc,
          contentObjectType,
          challengeText,
          contentObject,
        },
      },
    } = this.props

    let componentType = TAN_COMPONENT_TYPE_NONE
    if (challengeHhduc) {
      componentType = TAN_COMPONENT_TYPE_CHIPTAN
    } else if (contentObjectType) {
      componentType = TAN_COMPONENT_TYPE_PHOTOTAN
    }

    let tanComponent = null
    switch (componentType) {
      case TAN_COMPONENT_TYPE_CHIPTAN: {
        const { protocolData } = challengeHhduc
        tanComponent = <ChipTan data={protocolData} />
        break
      }
      case TAN_COMPONENT_TYPE_PHOTOTAN: {
        tanComponent = <PhotoTan content={contentObject} />
        break
      }
      default: { }
    }

    return (
      <div className={classes.tanComponent}>
        {tanComponent}
        <div className={classes.challengeText} dangerouslySetInnerHTML={{ __html: challengeText }} />
      </div>
    )
  }

  render() {
    const { loading, tanErrorMessage } = this.state

    const {
      classes,
      transaction: {
        provideTanRequest: {
          tanCallbackType,
        },
      },
    } = this.props

    const isDecoupled = isDecoupledTanCallback(tanCallbackType)

    const buttons = [
      <CancelButton key="cancel" type="cancel" label={lang.cancelButtonLabel} onClick={this.onCancel} />,
      <ButtonIconSquare
        key="confirm"
        customClass={classes.actionButton}
        type="submit"
        icon={IconCheck}
        label={isDecoupled ? lang.acceptButtonLabel : lang.confirmButtonLabel}
        onClick={this.onConfirm}
      />,
    ]

    const title = !isDecoupled ? lang.bankAccountSetup.tan.decoupledModalTitle : lang.bankAccountSetup.tan.decoupledModalTitle
    const subtitle = !isDecoupled ? lang.bankAccountSetup.tan.decoupledModalSubtitle : ''

    return (
      this.state.useModal
        ? (
          <ModalSmall
            title={title}
            subtitle={subtitle}
            actions={buttons}
            disableEscapeKeyDown
            disableBackdropClick
            open={true}
            onClose={this.onModalClose} >
            <Loader customClass={classes.loader} text={lang.bankAccountSetup.tan.tanWillBeChecked} visible={loading} />
            {this.renderTANComponent()}
            {!isDecoupled
              ? (
                <form className={classes.form} onSubmit={this.onSubmit}>
                  <BankingTextInput
                    autoFocus
                    name="tan"
                    label="TAN"
                    errorMessage={tanErrorMessage}
                    onChange={this.handleChange('tan')}
                  />
                </form>
              )
              : null}
          </ModalSmall>
        )
        : null
    )
  }
}

const mapStateToProps = state => {
  const {
    banking: {
      bankAccountList: { selectedBankAccount, contextMenu },
      bankAccountSetup,
    },
  } = state

  return {
    selectedBankAccount,
    contextMenu,
    bankAccountSetup,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    openBankAccountSetupTanInput: (provideTanRequest) => dispatch(openBankAccountSetupTanInput(provideTanRequest)),
    closeBankAccountSetupTanInput: () => dispatch(closeBankAccountSetupTanInput()),
  }
}

TanModal.defaultProps = {
  open: false,
}

const styles = {
  challengeText: {
    border: theme.border,
    borderRadius: 4,
    background: theme.backgroundColor,
    padding: 8,
    marginTop: 15,
    maxHeight: 100,
    overflowY: 'auto',
  },
  checkbox: {
    marginTop: 15,
  },
  checkboxLabel: {
    marginLeft: 15,
  },
  form: {
    paddingBottom: 60,
  },
  controls: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 40,
  },
  actionButton: {
    marginLeft: 15,
  },
  loader: {
    height: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  },
  tanComponent: {
    padding: '30px 0',
  },
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(TanModal))
